import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Card, CardContent, Menu, MenuItem, Skeleton, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import * as XLSX from 'xlsx'

import { useAppointmentsInRange } from '../api/analytics'

interface MonthlyCostPerOsaChartProps {
  env?: string
}

interface ChartData {
  month: string
  totalCost: number
  appointmentCount: number
  averageCost: number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)
}

const formatMonthLabel = (monthStr: string): string => {
  // monthStr is expected to be in format "YYYY-MM"
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export const MonthlyCostPerOsaChart: React.FC<MonthlyCostPerOsaChartProps> = ({ env = 'test' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Default to last month
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() - 1, 1) // First day of last month
  })
  const [toDate, setToDate] = useState<Date | null>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 0) // Last day of last month
  })

  // Format dates for API (ISO string)
  const fromDateStr = fromDate ? fromDate.toISOString() : ''
  const toDateStr = toDate ? toDate.toISOString() : ''

  // Fetch appointments in the date range
  const {
    data: appointmentsData,
    isLoading,
    error,
  } = useAppointmentsInRange(
    fromDateStr,
    toDateStr,
    'scheduleStartTime',
    'dispatched,completed',
    env
  )

  const appointments = appointmentsData?.data

  // Calculate monthly costs from appointments
  const chartData: ChartData[] = useMemo(() => {
    if (!appointments || appointments.length === 0) return []

    // Group appointments by month and sum costs
    const monthlyData = new Map<
      string,
      { totalCost: number; appointmentCount: number; costSum: number; countWithCost: number }
    >()

    appointments.forEach(appointment => {
      if (!appointment.scheduleStartTime) return

      const appointmentDate = new Date(appointment.scheduleStartTime)
      const monthKey = format(appointmentDate, 'yyyy-MM')

      const cost = appointment.costPerOSA || 0

      if (monthlyData.has(monthKey)) {
        const existing = monthlyData.get(monthKey)!
        existing.totalCost += cost
        existing.appointmentCount += 1
        if (cost > 0) {
          existing.costSum += cost
          existing.countWithCost += 1
        }
      } else {
        monthlyData.set(monthKey, {
          totalCost: cost,
          appointmentCount: 1,
          costSum: cost > 0 ? cost : 0,
          countWithCost: cost > 0 ? 1 : 0,
        })
      }
    })

    // Convert to array and sort by month
    return Array.from(monthlyData.entries())
      .map(([month, data]) => ({
        month,
        totalCost: data.totalCost,
        appointmentCount: data.appointmentCount,
        averageCost: data.countWithCost > 0 ? data.costSum / data.countWithCost : 0,
      }))
      .sort((a, b) => a.month.localeCompare(b.month))
  }, [appointments])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (exportFormat: 'csv' | 'xlsx') => {
    if (!appointments || appointments.length === 0) return

    // Filter appointments where costPerOSA > 0
    const appointmentsWithCost = appointments.filter(
      appointment => appointment.costPerOSA && appointment.costPerOSA > 0
    )

    if (appointmentsWithCost.length === 0) return

    const exportData = appointmentsWithCost.map(appointment => ({
      'Appointment UUID': appointment.uuid,
      'Building UUID': appointment.buildingUUID,
      'Customer Account UUID': appointment.customerAccountUUID,
      'Scout UUID': appointment.scoutUUID || '',
      'Scout Name': appointment.scoutName || '',
      'Customer Name': appointment.customerName || '',
      'Customer Address': appointment.customerAddress || '',
      'Customer Phone': appointment.customerPhone || '',
      'Customer Email': appointment.customerEmail || '',
      'Schedule Start Time': appointment.scheduleStartTime
        ? new Date(appointment.scheduleStartTime).toLocaleString('de-DE')
        : '',
      'Schedule End Time': appointment.scheduleEndTime
        ? new Date(appointment.scheduleEndTime).toLocaleString('de-DE')
        : '',
      Status: appointment.status,
      'Status Changed At': appointment.statusChangedAt
        ? new Date(appointment.statusChangedAt).toLocaleString('de-DE')
        : '',
      'Cost per OSA (EUR)': appointment.costPerOSA || 0,
      Environment: appointment.environment,
      'Residential Units': appointment.residentialUnits,
      'Confirmation Email Sent': appointment.confirmationEmailSent
        ? new Date(appointment.confirmationEmailSent).toLocaleString('de-DE')
        : '',
      Type: appointment.scoutCreated ? 'Privat' : appointment.type,
      'Created At': new Date(appointment.createdAt).toLocaleString('de-DE'),
      'Updated At': new Date(appointment.updatedAt).toLocaleString('de-DE'),
    }))

    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Appointments with Cost')

    const dateRange = `${fromDate ? format(fromDate, 'yyyy-MM-dd') : 'start'}_${toDate ? format(toDate, 'yyyy-MM-dd') : 'end'}`

    if (exportFormat === 'xlsx') {
      XLSX.writeFile(wb, `appointments-cost-per-osa-${dateRange}.xlsx`)
    } else {
      XLSX.writeFile(wb, `appointments-cost-per-osa-${dateRange}.csv`)
    }

    handleExportClose()
  }

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2D3748',
              mb: 3,
            }}
          >
            Monthly Cost per OSA
          </Typography>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    )
  }

  const maxValue = Math.max(...chartData.map(d => d.averageCost), 100)
  const yAxisMax = Math.ceil(maxValue / 100) * 100 // Round up to nearest 100

  return (
    <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#2D3748',
                mb: 2,
              }}
            >
              Monthly Cost per OSA
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {chartData.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 320,
            }}
          >
            <Typography color="error">Error loading monthly cost per OSA data</Typography>
          </Box>
        )}

        {!error && chartData.length > 0 && (
          <Box sx={{ width: '100%', height: 320, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 50,
                  bottom: 20,
                }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#718096', fontSize: 11 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  tickFormatter={formatMonthLabel}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: '#718096' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  domain={[0, yAxisMax]}
                  tickFormatter={(value: number) => formatCurrency(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#2D3748', fontWeight: 600 }}
                  labelFormatter={formatMonthLabel}
                  formatter={(value: number, name: string) => {
                    if (name === 'averageCost') {
                      return [formatCurrency(value), 'Average Cost']
                    }
                    return [value, name]
                  }}
                />
                <Bar dataKey="averageCost" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={60}>
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {!error && chartData.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 320,
            }}
          >
            <Typography color="textSecondary">No data available for the selected period</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default MonthlyCostPerOsaChart
